// CourseReviews — блок «Отзывы и оценки» на CourseDetailPage.
// ─────────────────────────────────────────────────────────────
// Структура:
//   • Sidebar-карточка: большая средняя оценка «4.7 / 5», звёзды,
//     X отзывов, гистограмма распределения по звёздам.
//   • Под ней — статус-баннер «Ваш отзыв на модерации» / «Доберите до 50%»
//     или форма «Оставить отзыв» / «Редактировать ваш отзыв».
//   • Список approved-отзывов с пагинацией «Показать ещё».
//
// Бэк отдаёт всё одним эндпоинтом /api/reviews/courses/:id — фронт
// не считает агрегаты, только рендерит. Денормализованный avgRating
// на Course кэширует бэк (post-save hook на approved отзыве).

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  Star,
  CheckCircle,
  Clock,
  AlertTriangle,
  Pencil,
  Trash2,
  User as UserIcon,
  Lock,
  Loader2,
} from 'lucide-react';
import {
  useGetCourseReviewsQuery,
  useGetMyReviewQuery,
  useCreateReviewMutation,
  useUpdateMyReviewMutation,
  useDeleteMyReviewMutation,
} from '../../api/apiSlice';
import { AVATAR_FALLBACK, setFallback } from '../../lib/placeholders';

// Универсальная строка из 5 звёзд под заданное значение. Half-star рисуем
// через CSS mask (overflow + width на основе value).
const StarRow = ({ value = 0, size = 'sm' }) => {
  const px = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="relative inline-flex items-center" aria-label={`${v} из 5`}>
      {/* серый фон */}
      <div className="flex">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} className={`${px} text-gray-300 dark:text-gray-600`} />
        ))}
      </div>
      {/* заполнение поверх */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${(v / 5) * 100}%` }}
        aria-hidden="true"
      >
        <div className="flex">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className={`${px} fill-amber-400 text-amber-400`} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Форма оставления/редактирования отзыва. controlled — родитель управляет
// initial-значениями (для edit) через key или props.
const ReviewForm = ({ initial, onSubmit, onCancel, busy }) => {
  const [rating, setRating] = useState(initial?.rating || 5);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState(initial?.text || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 10) {
      toast.error('Текст слишком короткий (мин. 10 символов)');
      return;
    }
    onSubmit({ rating, text: t });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 sm:p-6"
    >
      <h4 className="text-base font-semibold text-gray-900 dark:text-white">
        {initial ? 'Редактировать отзыв' : 'Оставить отзыв'}
      </h4>

      {/* Интерактивные звёзды */}
      <div className="mt-4 flex items-center gap-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">Оценка:</span>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              type="button"
              onMouseEnter={() => setHoverRating(s)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(s)}
              className="p-0.5"
              aria-label={`${s} из 5`}
            >
              <Star
                className={`w-7 h-7 transition ${
                  s <= (hoverRating || rating)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-gray-300 dark:text-gray-600'
                }`}
              />
            </button>
          ))}
        </div>
        <span className="ml-2 text-sm font-bold text-amber-600 tabular-nums">
          {rating} / 5
        </span>
      </div>

      <div className="mt-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Ваш отзыв
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={200}
          minLength={10}
          placeholder="Коротко: что понравилось или не понравилось? Поможет другим студентам выбрать."
          className="mt-2 w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
          <span>Минимум 10 символов</span>
          <span className={text.length > 200 ? 'text-red-500 font-semibold' : ''}>
            {text.length} / 200
          </span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-5 py-2.5 text-sm font-semibold transition"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {initial ? 'Сохранить' : 'Отправить на модерацию'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 transition"
          >
            Отмена
          </button>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Отзывы публикуются после проверки модератором
        </p>
      </div>
    </form>
  );
};

// Status-баннер для разных состояний gate'а.
const StatusBanner = ({ icon: Icon, tone, title, message, children }) => {
  const palette = {
    amber: 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300',
    emerald: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300',
    red: 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/20 text-red-700 dark:text-red-300',
    indigo: 'border-indigo-200 bg-indigo-50 dark:border-indigo-900/40 dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-300',
  }[tone];
  return (
    <div className={`rounded-2xl border ${palette} p-5 flex items-start gap-3`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold">{title}</h4>
        {message && <p className="text-sm mt-1 opacity-90">{message}</p>}
        {children}
      </div>
    </div>
  );
};

const CourseReviews = ({ courseId }) => {
  const { user } = useSelector((s) => s.auth);
  const [page, setPage] = useState(1);
  const [isEditing, setIsEditing] = useState(false);

  const { data: list, isLoading: listLoading } = useGetCourseReviewsQuery({
    courseId,
    page,
    limit: 10,
  }, { skip: !courseId });

  // Mine — только для залогиненного: гейт виден только своим, чужие не должны
  // дёргать /mine. skip если !user.
  const { data: mine } = useGetMyReviewQuery(courseId, {
    skip: !courseId || !user,
  });

  const [createReview, { isLoading: creating }] = useCreateReviewMutation();
  const [updateMyReview, { isLoading: updating }] = useUpdateMyReviewMutation();
  const [deleteMyReview, { isLoading: deleting }] = useDeleteMyReviewMutation();

  const handleCreate = async ({ rating, text }) => {
    try {
      await createReview({ courseId, rating, text }).unwrap();
      toast.success('Отзыв отправлен на модерацию');
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось отправить отзыв');
    }
  };

  const handleUpdate = async ({ rating, text }) => {
    try {
      await updateMyReview({
        reviewId: mine.review._id,
        courseId,
        rating,
        text,
      }).unwrap();
      toast.success('Отзыв обновлён и отправлен на повторную модерацию');
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось обновить отзыв');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить ваш отзыв? Это действие необратимо.')) return;
    try {
      await deleteMyReview({ reviewId: mine.review._id, courseId }).unwrap();
      toast.success('Отзыв удалён');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось удалить отзыв');
    }
  };

  const avgRating = list?.avgRating || 0;
  const total = list?.reviewsCount || 0;
  const distribution = list?.distribution || [0, 0, 0, 0, 0]; // [1★..5★]
  const items = list?.items || [];

  // Распределение по звёздам — гистограмма, сверху 5★ снизу 1★ (как у Amazon).
  const distMax = Math.max(...distribution, 1);

  return (
    <div className="space-y-6">
      {/* AGGREGATE: большая оценка + гистограмма */}
      <div className="grid gap-5 sm:grid-cols-[auto_1fr] rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 sm:p-6">
        <div className="text-center sm:text-left sm:border-r sm:border-gray-200 sm:dark:border-gray-700 sm:pr-6">
          <div className="text-5xl font-bold text-gray-900 dark:text-white tabular-nums">
            {avgRating.toFixed(1)}
          </div>
          <div className="mt-1">
            <StarRow value={avgRating} size="md" />
          </div>
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            из 5 · {total} {total === 1 ? 'отзыв' : 'отзывов'}
          </div>
        </div>

        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = distribution[star - 1] || 0;
            const pct = total ? (count / total) * 100 : 0;
            const barPct = (count / distMax) * 100;
            return (
              <div key={star} className="flex items-center gap-3 text-sm">
                <span className="w-8 text-gray-600 dark:text-gray-400 inline-flex items-center gap-0.5 shrink-0">
                  {star}
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                </span>
                <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
                <span className="w-16 text-right text-gray-500 dark:text-gray-400 tabular-nums shrink-0">
                  {count} ({Math.round(pct)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* MY REVIEW: статус-баннер или форма */}
      {user && mine && (
        <>
          {/* Гость / купил, но не дошёл до 50% */}
          {!mine.canReview && !mine.review && mine.reason === 'not_purchased' && (
            <StatusBanner
              icon={Lock}
              tone="amber"
              title="Только для студентов курса"
              message="Сначала купите курс — потом можно будет оставить отзыв."
            />
          )}
          {!mine.canReview && !mine.review && mine.reason === 'progress_too_low' && (
            <StatusBanner
              icon={Clock}
              tone="indigo"
              title={`Доберите до ${mine.gate}% прогресса`}
              message={`Сейчас у вас ${mine.progress}%. Отзыв доступен после половины курса — это защищает от drive-by отзывов.`}
            />
          )}

          {/* Уже есть отзыв — показываем статус и кнопки edit/delete */}
          {mine.review && !isEditing && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Ваш отзыв
                  </h4>
                  <div className="mt-1 flex items-center gap-2">
                    <StarRow value={mine.review.rating} />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {mine.review.rating} / 5
                    </span>
                  </div>
                </div>
                <ReviewStatusPill status={mine.review.status} />
              </div>
              <p className="mt-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line break-words">
                {mine.review.text}
              </p>
              {mine.review.status === 'rejected' && mine.review.rejectionReason && (
                <div className="mt-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 p-3 text-sm text-red-700 dark:text-red-300">
                  <span className="font-semibold">Причина отказа:</span>{' '}
                  {mine.review.rejectionReason}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 transition"
                >
                  <Pencil className="w-4 h-4" />
                  Редактировать
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-300 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/20 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 transition disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Удалить
                </button>
              </div>
            </div>
          )}

          {/* Можем — показываем форму создания / редактирования */}
          {mine.canReview && (!mine.review || isEditing) && (
            <ReviewForm
              initial={mine.review || null}
              onSubmit={mine.review ? handleUpdate : handleCreate}
              onCancel={isEditing ? () => setIsEditing(false) : null}
              busy={creating || updating}
            />
          )}
        </>
      )}

      {/* Гость (не залогинен) */}
      {!user && total === 0 && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-5 text-center text-sm text-gray-500 dark:text-gray-400">
          Пока нет отзывов на этот курс. Будьте первым, кто оставит отзыв
          после покупки.
        </div>
      )}

      {/* LIST */}
      <div className="space-y-3">
        {listLoading ? (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
          </div>
        ) : items.length === 0 ? (
          total > 0 ? null : null
        ) : (
          items.map((r) => <ReviewCard key={r._id} review={r} />)
        )}

        {list && list.page < list.pageCount && (
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 transition"
            >
              Показать ещё
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewStatusPill = ({ status }) => {
  const cfg = {
    pending: {
      icon: Clock,
      label: 'На модерации',
      cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    },
    approved: {
      icon: CheckCircle,
      label: 'Опубликован',
      cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    },
    rejected: {
      icon: AlertTriangle,
      label: 'Отклонён',
      cls: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
    },
  }[status] || { icon: Clock, label: status, cls: 'bg-gray-100 text-gray-700' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cfg.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
};

const ReviewCard = ({ review }) => {
  const date = new Date(review.createdAt).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <img
          src={review.author?.avatarUrl || AVATAR_FALLBACK}
          alt={review.author?.name}
          onError={setFallback(AVATAR_FALLBACK)}
          className="h-11 w-11 rounded-full object-cover shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-semibold text-gray-900 dark:text-white truncate">
                {review.author?.name || (
                  <span className="inline-flex items-center gap-1 text-gray-500">
                    <UserIcon className="w-4 h-4" /> Пользователь
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                <StarRow value={review.rating} />
                <span className="text-xs text-gray-500 dark:text-gray-400">{date}</span>
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line break-words">
            {review.text}
          </p>
        </div>
      </div>
    </div>
  );
};

export { CourseReviews as default, StarRow };
